import { ChevronLeft, ChevronRight } from "lucide-react";

function pageRange(page, totalPages) {
    const windowSize = 5;
    if (totalPages <= windowSize) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export default function PaginationBar({ total = 0, page = 1, limit = 25, onPageChange, className = "" }) {
    const totalPages = Math.max(1, Math.ceil((total || 0) / Math.max(1, limit || 1)));
    const pages = pageRange(page, totalPages);
    const start = total === 0 ? 0 : (page - 1) * limit + 1;
    const end = total === 0 ? 0 : Math.min(total, page * limit);

    return (
        <div className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-border text-sm ${className}`}>
            <div className="text-muted-foreground text-xs">
                {total === 0 ? "No records found" : `${start}–${end} of ${total}`}
                {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => onPageChange(Math.max(1, page - 1))}
                    className="h-8 px-2.5 inline-flex items-center gap-1 rounded border border-border disabled:opacity-40 hover:bg-secondary"
                >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">Prev</span>
                </button>
                {pages[0] > 1 && (
                    <>
                        <button type="button" onClick={() => onPageChange(1)} className="h-8 min-w-8 px-2 rounded border border-border hover:bg-secondary">1</button>
                        {pages[0] > 2 && <span className="px-1 text-muted-foreground">…</span>}
                    </>
                )}
                {pages.map((p) => (
                    <button
                        key={p}
                        type="button"
                        onClick={() => onPageChange(p)}
                        aria-current={p === page ? "page" : undefined}
                        className={`h-8 min-w-8 px-2 rounded border ${p === page ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
                    >
                        {p}
                    </button>
                ))}
                {pages[pages.length - 1] < totalPages && (
                    <>
                        {pages[pages.length - 1] < totalPages - 1 && <span className="px-1 text-muted-foreground">…</span>}
                        <button type="button" onClick={() => onPageChange(totalPages)} className="h-8 min-w-8 px-2 rounded border border-border hover:bg-secondary">{totalPages}</button>
                    </>
                )}
                <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                    className="h-8 px-2.5 inline-flex items-center gap-1 rounded border border-border disabled:opacity-40 hover:bg-secondary"
                >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
